/**
 * Spawner stashes: containers holding 64+ spawners, flagged by the plugin as likely dupe stashes.
 *
 * Detection only — the plugin removes nothing and punishes nobody. Staff use this to find the stash,
 * go there and deal with it, so the location and a one-click `/tp` lead everything else.
 *
 * Every value shown here is player-influenced text (names, and in principle anything in the payload).
 * It is only ever rendered as React text children, never as HTML.
 */

import { useEffect, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { fetchSpawnerStash, fetchSpawnerStashes } from "../api";
import { formatRelative, formatTimestamp } from "../format";
import { prettyMaterial } from "../minecraft";
import { IconClose } from "./Icons";
import type { LogRow, SpawnerStash } from "../types";

export const SPAWNER_STASH = "SPAWNER_STASH";

const UNKNOWN_PLACER = "Unknown (placed before tracking)";

function count(n: number | null): string {
  return n === null ? "?" : n.toLocaleString();
}

/** `placed_at` in the viewer's own zone, as asked for — the instant is unambiguous either way. */
function localTime(stash: SpawnerStash): string | null {
  if (stash.placed_at_ms !== null) {
    return new Date(stash.placed_at_ms).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "medium",
    });
  }
  return stash.placed_at;
}

function foundByText(stash: SpawnerStash): string {
  if (stash.found_by === "opened") return `Opened by ${stash.opened_by ?? "an unknown player"}`;
  if (stash.found_by === "chunk_load") return "Found when the chunk loaded";
  return stash.found_by ?? "Unknown";
}

/**
 * Copies the stash's `/tp` command. The command is also shown as plain text beside the button, so
 * it can still be selected by hand where the browser refuses clipboard access.
 */
export function CopyTpButton({ tp, compact = false }: { tp: string; compact?: boolean }): JSX.Element {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (state === "idle") return;
    const timer = setTimeout(() => setState("idle"), 1800);
    return () => clearTimeout(timer);
  }, [state]);

  return (
    <button
      type="button"
      className="btn stash-tp-btn"
      data-compact={compact}
      title={tp}
      onClick={async (event) => {
        event.stopPropagation();
        try {
          await navigator.clipboard.writeText(tp);
          setState("copied");
        } catch {
          setState("failed");
        }
      }}
    >
      {state === "copied" ? "Copied" : state === "failed" ? "Copy blocked" : "Copy /tp"}
    </button>
  );
}

function PlacedBy({
  stash,
  onFilterPlayer,
}: {
  stash: SpawnerStash;
  onFilterPlayer: (player: string) => void;
}): JSX.Element {
  if (!stash.placed_by) return <span className="who-none">{UNKNOWN_PLACER}</span>;
  return (
    <button
      type="button"
      className="who"
      title={`Show every log row for ${stash.placed_by}`}
      onClick={(event) => {
        event.stopPropagation();
        onFilterPlayer(stash.placed_by as string);
      }}
    >
      {stash.placed_by}
    </button>
  );
}

// ---- the detail card ----------------------------------------------------------------------------

interface ModalProps {
  /** The row's id; fetched when `stash` is not supplied. */
  id: number;
  /** Already-loaded record, from the Spawner Stashes page. */
  stash?: SpawnerStash;
  /** The log row it was opened from, so there is a message to fall back on if the fetch fails. */
  fallback?: LogRow;
  onClose: () => void;
  onFilterPlayer: (player: string) => void;
}

export function SpawnerStashModal({ id, stash, fallback, onClose, onFilterPlayer }: ModalProps): JSX.Element {
  const query = useQuery({
    queryKey: ["spawner-stash", id],
    queryFn: () => fetchSpawnerStash(id),
    enabled: stash === undefined,
    retry: 1,
  });
  const data = stash ?? query.data?.stash;

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal stash-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Spawner stash"
      >
        <div className="modal-head">
          <h2>Spawner stash</h2>
          <span className="tag stash-tag">dupe alert</span>
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>

        {data ? (
          <StashCard stash={data} onFilterPlayer={onFilterPlayer} />
        ) : query.isError ? (
          <>
            <div className="note" data-k="warn">
              Could not load this stash&rsquo;s details.
            </div>
            {fallback && <p className="sub">{fallback.message}</p>}
          </>
        ) : (
          <div className="item-none">Loading…</div>
        )}
      </div>
    </div>
  );
}

function StashCard({
  stash,
  onFilterPlayer,
}: {
  stash: SpawnerStash;
  onFilterPlayer: (player: string) => void;
}): JSX.Element {
  // Missing or malformed payload: the message is the whole story, so show that and nothing that
  // would pretend to know more.
  if (!stash.parsed) {
    return (
      <>
        <div className="note" data-k="warn">
          This row has no readable details, so only the plugin&rsquo;s summary is shown.
        </div>
        <p className="stash-message">{stash.message}</p>
        {stash.raw && (
          <div className="item-section">
            <h4>Stored details (unreadable)</h4>
            <div className="quote-block">{stash.raw}</div>
          </div>
        )}
        <dl className="facts" style={{ marginTop: 12 }}>
          <dt>Server</dt>
          <dd className="mono">{stash.server}</dd>
          <dt>Logged</dt>
          <dd className="mono">{formatTimestamp(stash.logged_at)}</dd>
        </dl>
      </>
    );
  }

  const placedAt = localTime(stash);

  return (
    <>
      <div className="item-section stash-location">
        <h4>Location</h4>
        <div className="stash-coords">{stash.coords ?? "Coordinates unknown"}</div>
        <div className="stash-where">
          <span>{stash.world ?? "unknown world"}</span>
          <span aria-hidden="true">·</span>
          <span className="mono">{stash.server}</span>
        </div>
        {stash.tp && (
          <div className="row" style={{ marginTop: 9 }}>
            <CopyTpButton tp={stash.tp} />
            <code className="stash-tp">{stash.tp}</code>
          </div>
        )}
      </div>

      <div className="item-section">
        <h4>Spawners</h4>
        <div className="stash-total">
          <b>{count(stash.spawners)}</b> spawner{stash.spawners === 1 ? "" : "s"}
        </div>
        <dl className="facts">
          <dt>In shulker boxes</dt>
          <dd>
            {count(stash.spawners_in_shulkers)}
            {stash.shulkers_with_spawners !== null &&
              ` (in ${stash.shulkers_with_spawners.toLocaleString()} shulker box${
                stash.shulkers_with_spawners === 1 ? "" : "es"
              })`}
          </dd>
          <dt>Loose</dt>
          <dd>{count(stash.loose_spawners)}</dd>
          <dt>Types</dt>
          <dd>
            {stash.spawner_types.length === 0 ? (
              <span className="who-none">{stash.spawner_types_raw ?? "not recorded"}</span>
            ) : (
              <ul className="stash-types">
                {stash.spawner_types.map((entry, index) => (
                  <li key={index}>
                    {entry.mob}
                    {entry.count !== null && <> &times;{entry.count.toLocaleString()}</>}
                  </li>
                ))}
              </ul>
            )}
          </dd>
        </dl>
      </div>

      <div className="item-section">
        <h4>Who and how</h4>
        <dl className="facts">
          <dt>Placed by</dt>
          <dd>
            <PlacedBy stash={stash} onFilterPlayer={onFilterPlayer} />
          </dd>
          {stash.placed_by && (
            <>
              <dt>Placed at</dt>
              <dd title={stash.placed_at ?? undefined}>{placedAt ?? "unknown"}</dd>
            </>
          )}
          <dt>Found by</dt>
          <dd>{foundByText(stash)}</dd>
          <dt>Container</dt>
          <dd>{stash.block ? prettyMaterial(stash.block) : "unknown"}</dd>
          <dt>Logged</dt>
          <dd className="mono">
            {formatTimestamp(stash.logged_at)}{" "}
            <span style={{ color: "var(--text-3)" }}>({formatRelative(stash.logged_at)})</span>
          </dd>
        </dl>
      </div>

      <p className="stash-message">{stash.message}</p>
    </>
  );
}

// ---- the page -----------------------------------------------------------------------------------

interface TabProps {
  /** SPAWNER_STASH rows arriving on the shared live stream, so a new stash appears without a refresh. */
  liveRows: LogRow[];
  onOpenPlayer: (player: string) => void;
  onFilterPlayer: (player: string) => void;
}

export function SpawnerStashTab({ liveRows, onOpenPlayer, onFilterPlayer }: TabProps): JSX.Element {
  const [open, setOpen] = useState<SpawnerStash | null>(null);

  const stashes = useInfiniteQuery({
    queryKey: ["spawner-stashes"],
    queryFn: ({ pageParam }) => fetchSpawnerStashes(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  // A live row only carries the summary, so refetch rather than splice in a half-parsed record.
  const liveCount = liveRows.length;
  useEffect(() => {
    if (liveCount === 0) return;
    const timer = setTimeout(() => void stashes.refetch(), 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveCount]);

  const rows = stashes.data?.pages.flatMap((page) => page.stashes) ?? [];

  return (
    <>
      <div className="filterbar">
        <span className="stash-blurb">
          Containers holding 64 or more spawners. Nothing has been removed — go there and deal with it.
        </span>
        <span className="grow" />
        <span className="conn" style={{ fontSize: 12 }}>
          {rows.length}
          {stashes.hasNextPage ? "+" : ""} stash{rows.length === 1 ? "" : "es"}
        </span>
      </div>

      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Server</th>
              <th>Coordinates</th>
              <th>Spawners</th>
              <th>Placed by</th>
              <th>Found by</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  {stashes.isLoading
                    ? "Loading…"
                    : stashes.isError
                      ? "Could not load spawner stashes."
                      : "No spawner stashes recorded yet."}
                </td>
              </tr>
            )}
            {rows.map((stash) => (
              <tr key={stash.id} onClick={() => setOpen(stash)} style={{ cursor: "pointer" }} title="Open stash">
                <td className="mono">
                  {formatTimestamp(stash.logged_at)}
                  <div style={{ fontSize: 10.5, color: "var(--text-3)" }}>{formatRelative(stash.logged_at)}</div>
                </td>
                <td className="mono">{stash.server}</td>
                <td>
                  {stash.parsed ? (
                    <div className="stash-cell-coords">
                      <span className="mono">{stash.coords ?? "?"}</span>
                      {stash.world && <span className="stash-cell-world">{stash.world}</span>}
                      {stash.tp && <CopyTpButton tp={stash.tp} compact />}
                    </div>
                  ) : (
                    <span style={{ color: "var(--text-2)" }}>{stash.message}</span>
                  )}
                </td>
                <td>
                  <b className="stash-cell-total">{count(stash.spawners)}</b>
                </td>
                <td>
                  {stash.parsed ? (
                    <PlacedBy stash={stash} onFilterPlayer={onFilterPlayer} />
                  ) : stash.player ? (
                    <button
                      type="button"
                      className="who"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenPlayer(stash.player as string);
                      }}
                    >
                      {stash.player}
                    </button>
                  ) : (
                    <span className="who-none">&mdash;</span>
                  )}
                </td>
                <td>{stash.parsed ? foundByText(stash) : <span className="who-none">&mdash;</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {stashes.hasNextPage && (
          <div style={{ padding: 14, textAlign: "center" }}>
            <button
              type="button"
              className="btn"
              disabled={stashes.isFetchingNextPage}
              onClick={() => void stashes.fetchNextPage()}
            >
              {stashes.isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>

      {open && (
        <SpawnerStashModal
          id={open.id}
          stash={open}
          onClose={() => setOpen(null)}
          onFilterPlayer={(player) => {
            setOpen(null);
            onFilterPlayer(player);
          }}
        />
      )}
    </>
  );
}
